import { useState, useEffect, useRef } from 'react';
import api from '../api';

const TYPE_CONFIG = {
  price:       { label: 'Цена',      color: '#3b82f6', icon: '💰' },
  spp:         { label: 'СПП',       color: '#8b5cf6', icon: '🏷️' },
  ad_spend:    { label: 'Реклама',   color: '#f59e0b', icon: '📢' },
  card_update: { label: 'Карточка',  color: '#10b981', icon: '📝' },
  buyout:      { label: 'Выкуп',     color: '#ef4444', icon: '📦' },
  manual:      { label: 'Заметка',   color: '#6b7280', icon: '✏️' },
  stock:       { label: 'Остатки',   color: '#06b6d4', icon: '📦' },
  orders:      { label: 'Заказы',    color: '#10b981', icon: '📋' },
};

function formatDetail(ev) {
  const t = TYPE_CONFIG[ev.type] || { label: ev.type, color: '#6b7280', icon: '📌' };
  let detail = '';
  if (ev.type === 'price') detail = `${ev.data?.from} → ${ev.data?.to} ₽`;
  else if (ev.type === 'spp') detail = `${ev.data?.from}% → ${ev.data?.to}%`;
  else if (ev.type === 'ad_spend') detail = `${Number(ev.data?.spend||0).toLocaleString()} ₽`;
  else if (ev.type === 'orders') detail = `${ev.data?.qty} шт · ${Number(ev.data?.sum||0).toLocaleString()} ₽`;
  else if (ev.type === 'buyout') detail = `${ev.data?.from}% → ${ev.data?.to}%`;
  else if (ev.type === 'stock' && ev.data?.event === 'zero') detail = `< 10 шт: ${(ev.data?.sizes||[]).join(', ')}`;
  else if (ev.type === 'stock' && ev.data?.event === 'restock') detail = `поставка: ${ev.data?.from} → ${ev.data?.to} шт`;
  else if (ev.type === 'card_update') detail = ev.data?.field === 'photo' ? 'фото изменено' : ev.data?.field === 'title' ? `заголовок изменён` : ev.data?.field === 'description' ? 'описание изменено' : `кол-во фото: ${ev.data?.from} → ${ev.data?.to}`;
  else if (ev.note) detail = ev.note;
  return { ...t, detail };
}

export function useHypothesisData(dates) {
  const [hypoData, setHypoData] = useState({});

  useEffect(() => {
    if (!dates || dates.length === 0) return;
    const from = dates[0];
    const to = dates[dates.length - 1];
    api.get(`/hypothesis/range/by-dates?from=${from}&to=${to}`)
      .then(r => { setHypoData(r.data); })
      .catch(() => {});
  }, [dates?.join(',')]);

  return hypoData;
}

const MONITORING_TYPES = ['card_update', 'stock', 'manual'];

export default function HypothesisTooltip({ nmId, date, hypoData, onAdded, filterTypes=null }) {
  const [show, setShow] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);
  const tooltipRef = useRef(null);

  const dateStr = date ? String(date).split('T')[0] : date;
  const key = `${nmId}__${dateStr}`;
  const allEvents = hypoData?.[key] || [];
  const events = filterTypes ? allEvents.filter(e => filterTypes.includes(e.type)) : allEvents;

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setShow(true), 300);
  };
  const handleMouseLeave = (e) => {
    clearTimeout(timerRef.current);
    if (tooltipRef.current && tooltipRef.current.contains(e.relatedTarget)) return;
    setShow(false);
    setEditMode(false);
    setNote('');
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/hypothesis/${nmId}`, { date: dateStr, note: note.trim(), type: 'manual' });
      setNote(''); setEditMode(false);
        if (onAdded) onAdded();
    } catch(e) {}
    setSaving(false);
  };

  const deleteEvent = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/hypothesis/entry/${id}`);
      if (onAdded) onAdded();
    } catch(e) {}
  };

  const hasDots = events.length > 0;

  return (
    <div style={{position:'relative',display:'inline-block'}}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>

      {/* Точки под датой */}
      {hasDots && (
        <div style={{display:'flex',gap:2,justifyContent:'center',marginTop:2}}>
          {events.slice(0,5).map((ev,i) => {
            const t = TYPE_CONFIG[ev.type] || { color: '#6b7280' };
            return <div key={i} style={{width:4,height:4,borderRadius:'50%',background:t.color}}/>;
          })}
          {events.length > 5 && <div style={{fontSize:7,color:'rgba(200,215,235,0.4)'}}>+{events.length-5}</div>}
        </div>
      )}

      {/* Тултип */}
      {show && (
        <div ref={tooltipRef}
          onMouseEnter={() => clearTimeout(timerRef.current)}
          onMouseLeave={handleMouseLeave}
          style={{position:'absolute',top:'calc(100% + 6px)',left:'50%',transform:'translateX(-50%)',
            background:'#1f2937',border:'1px solid rgba(255,255,255,0.12)',borderRadius:10,
            padding:'10px 12px',minWidth:220,maxWidth:280,zIndex:9999,
            boxShadow:'0 8px 30px rgba(0,0,0,0.7)',whiteSpace:'nowrap'}}>

          <div style={{fontSize:10,fontWeight:700,color:'rgba(200,215,235,0.4)',marginBottom:8}}>{date}</div>

          {events.length === 0 && !editMode && (
            <div style={{fontSize:11,color:'rgba(200,215,235,0.3)',marginBottom:8}}>Нет событий</div>
          )}

          {events.map((ev, i) => {
            const fmt = formatDetail(ev);
            return (
              <div key={i} style={{display:'flex',alignItems:'center',gap:6,marginBottom:5,
                padding:'4px 6px',background:'rgba(255,255,255,0.04)',borderRadius:6}}>
                <span style={{fontSize:12}}>{fmt.icon}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:10,color:fmt.color,fontWeight:600}}>{fmt.label}</div>
                  {fmt.detail && <div style={{fontSize:10,color:'rgba(200,215,235,0.5)',overflow:'hidden',textOverflow:'ellipsis'}}>{fmt.detail}</div>}
                </div>
                <button onClick={e=>deleteEvent(ev.id,e)}
                  style={{background:'none',border:'none',color:'rgba(239,68,68,0.4)',cursor:'pointer',fontSize:11,padding:2,flexShrink:0}}>✕</button>
              </div>
            );
          })}

          {editMode ? (
            <div style={{marginTop:8}}>
              <textarea value={note} onChange={e=>setNote(e.target.value)}
                placeholder="Заметка..." rows={2} autoFocus
                style={{width:'100%',background:'rgba(0,0,0,0.3)',border:'1px solid rgba(255,255,255,0.1)',
                  borderRadius:6,color:'#fff',fontSize:11,padding:'6px 8px',resize:'none',
                  boxSizing:'border-box',outline:'none',whiteSpace:'normal'}}/>
              <div style={{display:'flex',gap:6,marginTop:6}}>
                <button onClick={saveNote} disabled={saving}
                  style={{background:'rgba(59,130,246,0.25)',border:'1px solid rgba(59,130,246,0.5)',
                    color:'#60a5fa',fontSize:11,padding:'4px 12px',borderRadius:6,cursor:'pointer',fontWeight:700}}>
                  {saving?'...':'Сохранить'}
                </button>
                <button onClick={()=>{setEditMode(false);setNote('');}}
                  style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',
                    color:'rgba(200,215,235,0.5)',fontSize:11,padding:'4px 10px',borderRadius:6,cursor:'pointer'}}>
                  Отмена
                </button>
              </div>
            </div>
          ) : (
            <button onClick={()=>setEditMode(true)}
              style={{marginTop:6,background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.3)',
                color:'#60a5fa',fontSize:10,padding:'4px 10px',borderRadius:6,cursor:'pointer',width:'100%'}}>
              + Добавить заметку
            </button>
          )}
        </div>
      )}
    </div>
  );
}
